import React, { useCallback, useState, createContext } from "react";
import {Alert, ErrorDialog} from "../components";
import type { ProductUsage } from "../components/generic/ErrorDialog";

// Severity type definition
type Severity = "error" | "success" | "info" | "warning";

// Alert type definition
type Alert = {
  message: string;
  severity: Severity;
};


interface AppError {
  detailedMessage: string;
  traceId?: string | null;
  usage?: ProductUsage | null;
}

interface AlertState {
  alerts: Alert[];
  error: AppError | null;
}

interface AlertContextProps {
  addAlert: (message: string, severity?: Severity) => void;
  removeAlert: () => void;
  showError: (error: AppError) => void;
  clearError: () => void;
  error: AlertState["error"];
}

export const AlertContext = createContext<AlertContextProps>({
  addAlert: () => undefined,
  removeAlert: () => undefined,
  showError: () => undefined,
  clearError: () => undefined,
  error: null,
});

interface AlertProviderProps {
  children: React.ReactChild | React.ReactChildren;
}

function AlertProvider(props: AlertProviderProps) {
  const { children } = props;
  const [state, setState] = useState<AlertState>({
    alerts: [],
    error: null,
  });

  const addAlert = useCallback(
    (message: string, severity: Severity = "info") => {
      const newAlert = {
        message,
        severity,
      };
      setState((prev) => ({
        ...prev,
        alerts: [newAlert, ...prev.alerts],
      }));
    },
    []
  );

  const removeAlert = useCallback(() => {
    setState((prev) => ({
      ...prev,
      alerts: prev.alerts.slice(0, prev.alerts.length - 1),
    }));
  }, []);

  const showError = useCallback(
    (error: AppError) => {
      setState((prev) => ({
        ...prev,
        error,
      }));
    },
    []
  );

  const clearError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
  }, []);

  const activeAlert =
    state.alerts.length > 0 ? (
      <Alert
        severity={state.alerts[0].severity}
        message={state.alerts[0].message}
      />
    ) : null;

  return (
    <AlertContext.Provider
      value={{
        addAlert,
        removeAlert,
        showError,
        clearError,
        error: state.error,
      }}
    >
      {children}
      {activeAlert}
      <ErrorDialog
        open={!!state.error}
        onClose={clearError}
        error={state.error}
      />
    </AlertContext.Provider>
  );
}

export default AlertProvider;